import { definePreset, usePreset } from '@primeuix/themes';
import { defineStore } from 'pinia';

import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';
import Material from '@primeuix/themes/material';
import Nora from '@primeuix/themes/nora';
import { Theme } from '@primeuix/themes/types';
import { ref } from 'vue';

const BASE_PRESETS = {
  Aura,
  Material,
  Lara,
  Nora,
} as const;

export const BRAND_PALETTES = {
  purple: '#552080',
  blue: '#0f8beb',
  teal: '#14b8a6',
  green: '#22c55e',
  orange: '#f97316',
  indigo: '#6366f1',
} as const;

export const PRIMARY_SCALES: Record<string, Record<number, string>> = {
  purple: {
    50: '#f8f4f9',
    100: '#d6c9e0',
    200: '#b69fc8',
    300: '#9775b0',
    400: '#754a98',
    500: '#552080',
    600: '#3c165b',
    700: '#2a0741',
    800: '#ffffff',
    900: '#000000',
  },
  blue: {
    50: '#eff8ff',
    100: '#dbeffe',
    200: '#b8e0fd',
    300: '#7dc5fa',
    400: '#3aaaf6',
    500: '#0f8beb',
    600: '#0d73d0',
    700: '#0c5eac',
    800: '#ffffff',
    900: '#000000',
  },
  teal: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
    800: '#ffffff',
    900: '#000000',
  },
  green: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#ffffff',
    900: '#000000',
  },
  orange: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316',
    600: '#ea580c',
    700: '#c2410c',
    800: '#ffffff',
    900: '#000000',
  },
  indigo: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#ffffff',
    900: '#000000',
  },
};

function buildPrimaryScale(brand = 'purple') {
  return PRIMARY_SCALES[brand] ?? PRIMARY_SCALES['purple'];
}

function buildSecondaryScale() {
  return {
    100: `#F4F3F5`,
    200: `#D9D8DE`,
    300: `#C6C6CE`,
    400: `#B3B4BF`,
    500: `#9d9fAf`,
    600: `#7f8288`,
  };
}

type ComponentsOverrides = Partial<Theme['preset']>;

function buildComponentOverrides(): ComponentsOverrides {
  return {
    // 3) component overrides
    components: {
      /* BUTTON */
      button: {
        colorScheme: {
          dark: {
            root: {
              primary: {
                background: '{primary.500}',
                color: '{primary.800}',
                hoverBackground: '{primary.600}',
                hoverColor: '{primary.800}',
                activeBackground: '{primary.500}',
                activeBorderColor: '{primary.500}',
                activeColor: '{primary.800}',
                focusRing: {
                  color: '{semantic.focusRing.color}'
                },
              },
              secondary: {
                background: '{secondary.600}',
                color: '{primary.800}',
                hoverBackground: '{secondary.500}',
                hoverColor: '{primary.800}',
                activeBackground: '{secondary.600}',
                activeBorderColor: '{secondary.600}',
                activeColor: '{primary.800}',
                focusRing: {
                  color: '{semantic.focusRing.color}'
                }
              }
            }
          },
          light: {
            root: {
              primary: {
                background: '{primary.600}',
                color: '{primary.800}',
                hoverBackground: '{primary.700}',
                hoverColor: '{primary.800}',
                activeBackground: '{primary.600}',
                activeBorderColor: '{primary.600}',
                activeColor: '{primary.800}',
                focusRing: {
                  color: '{semantic.focusRing.color}'
                }
              },
              secondary: {
                background: '{secondary.300}',
                color: '{primary.900}',
                hoverBackground: '{secondary.200}',
                hoverColor: '{primary.900}',
                activeBackground: '{secondary.300}',
                activeBorderColor: '{secondary.300}',
                activeColor: '{primary.900}',
                focusRing: {
                  color: '{semantic.focusRing.color}'
                }
              }
            }
          }
        },
        root: {
          primary: {
            background: '{primary.600}',
            color: '{primary.800}',
            hoverBackground: '{primary.700}',
            hoverColor: '{primary.800}',
            activeBackground: '{primary.600}',
            activeBorderColor: '{primary.600}',
            activeColor: '{primary.800}',
            focusRing: {
              color: '{semantic.focusRing.color}'
            }
          },
          secondary: {
            background: '{secondary.300}',
            color: '{primary.900}',
            hoverBackground: '{secondary.200}',
            hoverColor: '{primary.900}',
            activeBackground: '{secondary.300}',
            activeBorderColor: '{secondary.300}',
            activeColor: '{primary.900}',
            focusRing: {
              color: '{semantic.focusRing.color}'
            }
          },
          focusRing: {
            width: '{semantic.focusRing.width}',
            style: 'solid',
            offset: '1px'
          },
          borderRadius: '0.5rem',
          paddingX: '1.5rem',
          paddingY: '0.5rem'
        },
        text: {
          primary: {
            color: '{primary.color}',
            hoverBackground: '{primary.50}',
            activeBackground: '{primary.100}'
          },
          secondary: {
            color: '{text.color}',
            hoverBackground: '{surface.50}',
            activeBackground: '{surface.100}'
          }
        },
        link: {
          color: '{primary.color}',
          hoverColor: '{primary.500}',
          activeColor: '{primary.500}'
        }
      },

      selectbutton: {
        colorScheme: {
          dark: {
            root: {
              borderRadius: "1rem",
              invalidBorderColor: "red"
            }
          },
          light: {
            root: {
              borderRadius: "1rem",
              invalidBorderColor: "red"
            }
          }
        },
      },

      /* BUTTONGROUP */
      buttongroup: {
        root: {
          background: '{primary.600}',
        },
        button: {
          borderRadius: '1rem',
          background: '{primary.600}',
          color: '{primary.800}',
          hoverBackground: '{primary.700}'
        }
      },

      timeline: {

        event: {
          minHeight: "4rem",
        },
        eventConnector: {
          color: "{primary.100}",
          size: "0.3rem"
        },
        eventMarker: {
          background: "var(--primary-500)",
          borderColor: "var(--primary-500)",
          borderRadius: "50%",
          borderWidth: "2px",
          content: {
            background: "var(--surface-0)",
            borderRadius: "50%",
            insetShadow: "0 0 0 2px var(--primary-500)",
            size: "0.75rem"
          },
          size: "1.25rem"
        },
        horizontal: {
          eventContent: {
            padding: "1rem 1.25rem"
          }
        },
        vertical: {
          eventContent: {
            padding: "0.75rem 1rem"
          }
        }

      },

      /* INPUTTEXT */
      inputtext: {
        colorScheme: {
          dark: {
            root: {
              background: "{primary.700}",
              color: "{primary.100}",
              placeholderColor: "{primary.300}",

              borderColor: "{primary.500}",
              hoverBorderColor: "{primary.400}",
              focusBorderColor: "{primary.300}",

              invalidBorderColor: "{red.400}",
              invalidPlaceholderColor: "{red.300}",

              disabledBackground: "{secondary.600}",
              disabledColor: "{secondary.300}",

              filledBackground: "{primary.600}",
              filledHoverBackground: "{primary.600}",
              filledFocusBackground: "{primary.600}",
            }
          },
          light: {
            root: {
              background: "{primary.800}",
              color: "{primary.900}",
              placeholderColor: "{secondary.400}",

              borderColor: "{primary.500}",
              hoverBorderColor: "{primary.600}",
              focusBorderColor: "{primary.500}",

              invalidBorderColor: "{red.500}",
              invalidPlaceholderColor: "{red.400}",

              disabledBackground: "{secondary.100}",
              disabledColor: "{secondary.500}",

              filledBackground: "{primary.50}",
              filledHoverBackground: "{primary.100}",
              filledFocusBackground: "{primary.50}",
            }
          }
        },
        root: {
          borderRadius: '0.5rem',
          paddingX: '0.4rem',
          paddingY: '0.4rem',
        }
      },

      /* INPUTNUMBER */
      inputnumber: {
        button: {
          background: '{surface.0}',
          color: '{text.muted.color}',
          borderColor: '{surface.border.color}',
          borderRadius: '0',
          hoverBackground: '{surface.50}',
          hoverColor: '{primary.color}',
          hoverBorderColor: '{primary.hoverColor}',
          activeBackground: '{surface.100}',
          activeBorderColor: '{surface.border.color}',
          activeColor: '{primary.color}',
          verticalPadding: '{spacing.1}'
        }
      },

      /* INPUTOTP */
      inputotp: {
        root: {
          gap: '{spacing.2}'
        }
      },

      /* FLOATLABEL */
      floatlabel: {
        root: {
          color: '{text.color}',
          activeColor: '{primary.color}',
          focusColor: '{primary.color}',
          invalidColor: '{primary.color}'
        }
      },

      /* TEXTAREA */
      textarea: {
        colorScheme: {
          dark: {
            root: {
              background: '{primary.700}',
              color: '{primary.100}',
              placeholderColor: '{primary.300}',
              borderColor: '{primary.500}',
              hoverBorderColor: '{primary.400}',
              focusBorderColor: '{primary.300}',
              invalidBorderColor: '{red.400}',
              disabledBackground: '{secondary.600}',
              disabledColor: '{secondary.300}',
              filledBackground: '{primary.600}',
              filledHoverBackground: '{primary.600}',
              filledFocusBackground: '{primary.600}',
            }
          },
          light: {
            root: {
              background: '{primary.800}',
              color: '{primary.900}',
              placeholderColor: '{secondary.400}',
              borderColor: '{primary.200}',
              hoverBorderColor: '{primary.400}',
              focusBorderColor: '{primary.500}',
              invalidBorderColor: '{red.500}',
              disabledBackground: '{secondary.100}',
              disabledColor: '{secondary.500}',
              filledBackground: '{primary.50}',
              filledHoverBackground: '{primary.100}',
              filledFocusBackground: '{primary.50}',
            }
          }
        },
        root: {
          borderRadius: '0.5rem',
          focusRing: {
            color: '{semantic.focusRing.color}'
          },
        }
      },

      /* SELECT (new v4) */
      select: {
        colorScheme: {
          dark: {
            root: {
              background: '{primary.700}',
              borderColor: '{primary.500}',
              color: '{primary.100}',
              placeholderColor: '{primary.300}',
              disabledBackground: '{secondary.600}',
              disabledColor: '{secondary.300}',
              filledBackground: '{primary.600}',
              filledFocusBackground: '{primary.600}',
              filledHoverBackground: '{primary.600}',
            },
            overlay: {
              background: '{primary.700}',
              borderColor: '{primary.600}',
              color: '{primary.100}',
            },
            option: {
              color: '{primary.100}',
              focusBackground: '{primary.600}',
              focusColor: '{primary.100}',
              selectedBackground: '{primary.500}',
              selectedColor: '{primary.800}',
              selectedFocusBackground: '{primary.600}',
              selectedFocusColor: '{primary.100}',
            }
          },
          light: {
            root: {
              background: '{primary.800}',
              borderColor: '{secondary.500}',
              color: '{primary.900}',
              placeholderColor: '{secondary.400}',
              disabledBackground: '{secondary.100}',
              disabledColor: '{secondary.500}',
              filledBackground: '{primary.50}',
              filledFocusBackground: '{primary.50}',
              filledHoverBackground: '{primary.100}',
            },
            overlay: {
              background: '{primary.800}',
              borderColor: '{primary.200}',
              color: '{primary.900}',
            },
            option: {
              color: '{primary.900}',
              focusBackground: '{primary.100}',
              focusColor: '{primary.900}',
              selectedBackground: '{primary.100}',
              selectedColor: '{primary.700}',
              selectedFocusBackground: '{primary.100}',
              selectedFocusColor: '{primary.700}',
            }
          }
        },
        root: {
          borderRadius: '0.5rem',
        },
        overlay: {
          borderRadius: '0.5rem',
        },
        option: {
          borderRadius: '0.5rem',
        }
      },

      /* DATE PICKER */
      datepicker: {
        buttonbar: {
          borderColor: "{primary.200}"
        },
        date: {
          color: "{primary.900}",
          selectedColor: "{primary.900}",
          selectedBackground: "{primary.100}",

          rangeSelectedColor: "{primary.700}",
          rangeSelectedBackground: "{primary.100}",

          hoverColor: "{primary.700}",
          hoverBackground: "{primary.100}",
        },
        dropdown: {
          color: "{primary.400}",
          background: "{primary.900}",

          activeColor: "{primary.500}",
          activeBackground: "{primary.100}",
          activeBorderColor: "{primary.100}",

          borderColor: "{primary.900}",
          borderRadius: "0.5rem",

        },
        header: {
          background: "{primary.800}",
          borderColor: "{primary.800}",
          color: "{primary.900}",
        },
        inputIcon: {
          color: "{primary.500}",
        },
        panel: {
          background: "{primary.800}",
          borderColor: "{primary.800}",
          borderRadius: "0.3rem",
          color: "{primary.900}",
        },
        selectMonth: {
          borderRadius: "0.3rem",
          color: "{primary.900}",
          hoverBackground: "{primary.800}",
          hoverColor: "{primary.700}",
        },
        selectYear: {
          borderRadius: "0.3rem",
          color: "{primary.900}",
          hoverBackground: "{primary.800}",
          hoverColor: "{primary.700}",
        },
        timePicker: {
          borderColor: "{primary.200}",
        },
      },


      multiselect: {
        colorScheme: {
          light: {
            root: {
              // base
              background: '{primary.50}',
              color: '{primary.700}',
              borderColor: '{surface.border.color}',
              borderRadius: '0.5rem',

              // stati filled
              filledBackground: '{primary.50}',
              filledHoverBackground: '{primary.100}',
              filledFocusBackground: '{primary.100}',

              // stati hover / focus
              hoverBorderColor: '{primary.300}',
              focusBorderColor: '{primary.400}',

              // disabled
              disabledBackground: '{surface.100}',
              disabledColor: '{primary.900}',

              // invalid
              invalidBorderColor: '{primary.500}',
              invalidPlaceholderColor: '{primary.500}',

              // placeholder
              placeholderColor: '{primary.900}'
            }
          },
          dark: {
            root: {
              // base
              background: '{surface.900}',
              color: '{primary.700}',
              borderColor: '{primary.700}',
              borderRadius: '{border.radius.lg}',

              // stati filled
              filledBackground: '{primary.900}',
              filledHoverBackground: '{primary.800}',
              filledFocusBackground: '{primary.700}',

              // stati hover / focus
              hoverBorderColor: '{primary.500}',
              focusBorderColor: '{primary.400}',

              // disabled
              disabledBackground: '{surface.800}',
              disabledColor: '{primary.900}',

              // invalid
              invalidBorderColor: '{primary.300}',
              invalidPlaceholderColor: '{primary.300}',

              // placeholder
              placeholderColor: '{primary.900}'
            }
          }
        },
        overlay: {
          borderRadius: "{border.radius.xl}",
          background: '{primary.50}',
          borderColor: '{surface.border.color}',
          color: '{primary.900}'
        },
        dropdown: {
          color: "{primary.700}",
        },
        option: {
          color: '{primary.700}',
          borderRadius: '{border.radius.lg}',
          focusBackground: '{primary.100}',
          focusColor: '{primary.900}',
          selectedBackground: '{primary.50}',
          selectedColor: '{primary.700}',
          selectedFocusBackground: '{primary.100}',
          selectedFocusColor: '{primary.700}'
        }
      },


      /* RADIOBUTTON */
      radiobutton: {
        root: {
          background: '{primary.200}',
          borderColor: '{surface.border.color}',

          checkedBorderColor: '{primary.200}',
          checkedBackground: '{primary.200}',

          checkedDisabledBorderColor: '{surface.300}',
          checkedFocusBorderColor: '{primary.200}',
          checkedHoverBackground: '{primary.200}',
          checkedHoverBorderColor: '{primary.200}',

          disabledBackground: '{surface.100}',
          filledBackground: '{primary.500}',
          focusBorderColor: '{primary.color}',

          focusRing: {
            color: '{primary.500}',
          },
          hoverBorderColor: '{primary.hoverColor}'
        }
      },

      /* CHECKBOX */
      checkbox: {
        root: {
          background: '{primary.50}',
          borderColor: '{primary.100}',
          borderRadius: '4px',

          checkedBackground: '{primary.200}',
          checkedBorderColor: '{primary.200}',
          checkedFocusBorderColor: '{primary.200}',
          checkedHoverBackground: '{primary.200}',
          checkedHoverBorderColor: '{primary.200}',

          disabledBackground: '{surface.100}',
          filledBackground: '{primary.100}',
          focusBorderColor: '{primary.color}',
          focusRing: {
            color: '{semantic.focusRing.color}'
          },
          hoverBorderColor: '{primary.hoverColor}'
        }
      },

      /* TOGGLEBUTTON */
      togglebutton: {
        root: {
          background: '{surface.0}',
          color: '{text.color}',
          borderColor: '{surface.border.color}',
          checkedBackground: '{primary.color}',
          checkedColor: '{primary.contrast.color}',
          checkedBorderColor: '{primary.color}',
          hoverBackground: '{surface.50}'
        },
      },

      /* TOGGLESWITCH */
      toggleswitch: {
        colorScheme: {
          dark: {
            root: {
              borderRadius: '1rem',
              borderWidth: '0.125rem',

              background: '{primary.800}',
              borderColor: '{primary.600}',

              hoverBackground: '{primary.300}',
              hoverBorderColor: '{primary.300}',

              checkedBackground: '{primary.500}',
              checkedBorderColor: '{primary.500}',

              checkedHoverBackground: '{primary.300}',
              checkedHoverBorderColor: '{primary.300}',
              disabledBackground: '{primary.900}',
              invalidBorderColor: '{red.400}',
              width: '3rem',
              height: '1.75rem',
              transitionDuration: '0.2s',
              shadow: '',
              slideDuration: '0.2s',
              focusRing: {
                shadow: '',
                width: '2px',
                color: '{primary.400}',
                offset: '2px',
                style: 'solid'
              },
            },
            handle: { // circle of toggle switch
              borderRadius: '50%',
              size: '1.25rem',
              background: '{primary.100}',
              color: '{primary.900}',
              hoverBackground: '{primary.300}',
              hoverColor: '{primary.900}',
              checkedBackground: '{primary.900}',
              checkedColor: '{primary.100}',
              checkedHoverBackground: '{primary.300}',
              checkedHoverColor: '{primary.300}',
              disabledBackground: '{primary.500}',
            }
          },
          light: {
            root: {
              borderRadius: '1rem',
              borderWidth: '0.125rem',

              background: '{primary.300}',
              borderColor: '{primary.300}',

              hoverBackground: '{primary.300}',
              hoverBorderColor: '{primary.300}',

              checkedBackground: '{primary.500}',
              checkedBorderColor: '{primary.500}',

              checkedHoverBackground: '{primary.300}',
              checkedHoverBorderColor: '{primary.300}',

              disabledBackground: '{primary.100}',
              invalidBorderColor: '{red.500}',
              width: '3rem',
              height: '1.75rem',
              transitionDuration: '0.2s',
              shadow: '',
              slideDuration: '0.2s',
              focusRing: {
                shadow: '',
                width: '2px',
                color: '{primary.800}',
                offset: '2px',
                style: 'solid'
              },
            },
            handle: { // circle of toggle switch
              borderRadius: '50%',
              size: '1.25rem',
              background: '{primary.800}',
              color: '{primary.800}',

              checkedColor: '{primary.800}',
              checkedBackground: '{primary.800}',

              hoverColor: '{primary.800}',
              hoverBackground: '{primary.800}',

              checkedHoverBackground: '{primary.800}',
              checkedHoverColor: '{primary.800}',

              disabledBackground: '{secondary.100}',
            }
          }
        },
        root: {
          borderRadius: '1rem',
          borderWidth: '0.125rem',

          background: '{primary.300}',
          borderColor: '{primary.300}',

          hoverBackground: '{primary.300}',
          hoverBorderColor: '{primary.300}',

          checkedBackground: '{primary.500}',
          checkedBorderColor: '{primary.500}',

          checkedHoverBackground: '{primary.300}',
          checkedHoverBorderColor: '{primary.300}',

          disabledBackground: '{primary.100}',
          invalidBorderColor: '{red.500}',
          width: '3rem',
          height: '1.75rem',
          transitionDuration: '0.2s',
          shadow: '',
          slideDuration: '0.2s',
          focusRing: {
            shadow: '',
            width: '2px',
            color: '{primary.800}',
            offset: '2px',
            style: 'solid'
          },
        },
        handle: { // circle of toggle switch
          borderRadius: '50%',
          size: '1.25rem',
          background: '{primary.800}',
          color: '{primary.800}',

          checkedColor: '{primary.800}',
          checkedBackground: '{primary.800}',

          hoverColor: '{primary.800}',
          hoverBackground: '{primary.800}',

          checkedHoverBackground: '{primary.800}',
          checkedHoverColor: '{primary.800}',

          disabledBackground: '{primary.100}',
        }
      },

      /* DRAWER */
      drawer: {
        colorScheme: {
          dark: {
            root: {
              background: "{primary.700}",
              borderColor: "{primary.700}",
              color: "white",
            }
          },
          light: {
            root: {
              background: "{primary.700}",
              borderColor: "{primary.700}",
              color: "white",
            }
          }
        },
      },

      /* MENU */
      menu: {
        colorScheme: {
          dark: {
            root: {
              background: "{primary.700}",
              borderColor: "{primary.600}",
              color: "{primary.100}",
              borderRadius: "{border.radius.xl}",
            }
          },
          light: {
            root: {
              background: "{primary.800}",
              borderColor: "{primary.50}",
              color: "{primary.500}",
              borderRadius: "{border.radius.xl}"
            }
          },
        },
        submenuLabel: {
          padding: "0rem"
        }
      },

      /* TOOLTIP */
      tooltip: {
        root: {
          borderRadius: "0.5rem"
        },
        colorScheme: {
          dark: {
            root: {
              background: "{primary.200}",
              color: "{primary.900}",
            }
          },
          light: {
            root: {
              background: "{primary.200}",
              color: "{primary.900}",
            }
          }
        }
      },

      skeleton: {
        colorScheme: {
          dark: {
            root: {
              background: "var(--p-primary-100)",
              borderRadius: "1rem"
            }
          },
          light: {
            root: {
              background: "var(--p-primary-100)",
              borderRadius: "1rem"
            }
          }
        },
        root: {
          background: "var(--p-primary-100)",
          borderRadius: "1rem"
        }
      },

      /* ACCORDION */
      accordion: {
        colorScheme: {
          light: {
            header: {
              background: '{primary.500}',
              color: '{primary.800}',
              hoverBackground: '{primary.600}',
              activeBackground: '{primary.600}',
              activeColor: "{primary.800}",
              activeHoverBackground: "{primary.600}",
              activeHoverColor: "{primary.800}",
              borderColor: "{surface.0}",
              borderRadius: "0.8rem",
              borderWidth: "0.05rem",
              fontWeight: "",
              hoverColor: "{primary.800}",
              padding: "0rem 2rem 0rem 2rem",
              first: {
                borderWidth: "0rem",
                topBorderRadius: "0.8rem",
              },
              focusRing: {
                color: "{primary.800}",
              },
              last: {
                activeBottomBorderRadius: "0.8rem",
                bottomBorderRadius: "0.8rem"
              },
            },
            content: {
              background: "transparent",
              borderColor: "transparent",
              borderWidth: "1rem",
              color: "{primary.900}",
              padding: "0rem"
            },
            panel: {
              borderColor: "{primary.700}",
              borderWidth: "0rem"
            },
          },
          dark: {
            header: {
              background: '{primary.600}',
              color: '{primary.100}',
              hoverBackground: '{primary.500}',
              activeBackground: '{primary.500}',
              activeColor: '{primary.100}',
              activeHoverBackground: '{primary.500}',
              activeHoverColor: '{primary.100}',
              borderColor: '{primary.700}',
              borderRadius: '0.8rem',
              borderWidth: '0.05rem',
              fontWeight: '',
              hoverColor: '{primary.100}',
              padding: '0rem 2rem 0rem 2rem',
              first: {
                borderWidth: '0rem',
                topBorderRadius: '0.8rem',
              },
              focusRing: {
                color: '{primary.300}',
              },
              last: {
                activeBottomBorderRadius: '0.8rem',
                bottomBorderRadius: '0.8rem'
              },
            },
            content: {
              background: 'transparent',
              borderColor: 'transparent',
              borderWidth: '1rem',
              color: '{primary.100}',
              padding: '0rem'
            },
            panel: {
              borderColor: '{primary.600}',
              borderWidth: '0rem'
            },
          }
        }

      },


      /* DATATABLE */
      datatable: {

        colorScheme: {
          dark: {
            headerCell: {
              background: '{primary.600}',
              borderColor: '{primary.700}',
              color: '{primary.100}',
              hoverBackground: '{primary.700}',
              selectedBackground: '{primary.600}',
              selectedColor: '{primary.100}',
            },
            row: {
              stripedBackground: '{surface.800}',
              background: '{surface.900}',
              color: '{primary.100}',
              hoverBackground: '{surface.800}',
              hoverColor: '{primary.100}',
            },
            header: {
              background: '{primary.700}',
              color: '{primary.100}',
              borderColor: '{primary.600}'
            },
          },
          light: {
            row: {
              stripedBackground: '{primary.50}',
            }
          }
        },

        bodyCell: {
          borderColor: '{surface.border.color}',
          selectedBorderColor: '{primary.color}',
        },
        headerCell: {
          background: '{primary.700}',
          borderColor: '{primary.700}',
          color: '{primary.800}',
          hoverBackground: '{primary.800}',
          selectedBackground: '{primary.700}',
          selectedColor: '{primary.contrast.color}',
          focusRing: {
            color: '{semantic.focusRing.color}',
          }
        },
        row: {
          stripedBackground: '{surface.500}',
          background: '{surface.0}',
          color: '{text.color}',
          hoverBackground: '{surface.50}',
          hoverColor: '{text.color}',
          selectedBackground: 'rgba(148, 27, 235, 0.12)',
          selectedColor: '{primary.color}',
          focusRing: {
            color: '{semantic.focusRing.color}',
          }
        },

        root: {
          borderColor: '{surface.border.color}',
        },

        header: {
          background: '{primary.900}',
          color: '{primary.contrast.color}',
          borderColor: '{primary.700}'
        },

        footer: {
          borderColor: '{surface.border.color}',
          background: '{surface.50}',
          color: '{text.color}',
        }
      },

      /* DIVIDER */
      divider: {
        root: {
          borderColor: '{primary.200}',
        },
        colorScheme: {
          dark: {
            root: {
              borderColor: "{primary.600}"
            }
          },
          light: {
            root: {
              borderColor: "{primary.200}"
            }
          }
        }
      },

      /* CARD */
      card: {
        colorScheme: {
          dark: {
            root: {
              background: "{primary.700}",
              color: "{primary.50}",
              borderRadius: "{border.radius.md}"
            }
          },
          light: {
            root: {
              background: "{primary.50}",
              color: "{primary.700}",
              borderRadius: "{border.radius.md}"
            }
          }
        },
        root: {
          background: '{primary.50}',
          color: '{primary.700}',
          borderRadius: '{border.radius.xl}',
          shadow: '{shadow.2}'
        },
        body: {
          gap: '{spacing.3}',
          padding: '{spacing.4}',
        }
      },

      /* BADGE */
      badge: {
        colorScheme: {
          dark: {
            primary: {
              background: '{primary.100}',
              color: '{primary.900}'
            }
          },
          light: {
            primary: {
              background: '{primary.100}',
              color: '{primary.900}'
            }
          }
        },
        root: {
          borderRadius: "0.5rem"
        }
      },

      /* DIALOG */
      dialog: {
        colorScheme: {
          dark: {
            root: {
              background: "{primary.700}",
              borderColor: "{primary.600}",
              borderRadius: "{border.radius.xl}",
              color: "{primary.100}"
            }
          },
          light: {
            root: {
              background: "{primary.50}",
              borderColor: "{primary.200}",
              borderRadius: "{border.radius.xl}",
              color: "{primary.900}"
            }
          }
        },
      },

      /* TOAST */

      /* MESSAGE */

      /* TAG */
      tag: {
        colorScheme: {
          dark: {
            primary: {
              background: '{primary.400}',
              color: '{primary.900}'
            }
          },
          light: {
            primary: {
              background: '{primary.500}',
              color: '{primary.800}'
            }
          }
        },
        root: {
          borderRadius: '1rem',
          padding: '0.2rem 0.6rem',
        }
      },

      /* CHIP */
      chip: {
        colorScheme: {
          dark: {
            root: {
              background: '{primary.600}',
              color: '{primary.100}',
            }
          },
          light: {
            root: {
              background: '{primary.100}',
              color: '{primary.700}',
            }
          }
        },
        root: {
          borderRadius: '1rem',
          paddingX: '0.75rem',
          paddingY: '0.25rem',
        }
      },

      /* PAGINATOR */
      paginator: {
        colorScheme: {
          dark: {
            root: {
              background: '{primary.700}',
              color: '{primary.100}',
            },
            navButton: {
              selectedBackground: '{primary.500}',
              selectedColor: '{primary.900}',
              hoverBackground: '{primary.600}',
            }
          },
          light: {
            navButton: {
              selectedBackground: '{primary.500}',
              selectedColor: '{primary.800}',
              hoverBackground: '{primary.100}',
            }
          }
        }
      },

      /* TABS */
      tabs: {
        colorScheme: {
          dark: {
            activeBar: { background: '{primary.400}' },
            tab: {
              color: '{primary.300}',
              activeColor: '{primary.100}',
              hoverColor: '{primary.200}',
              borderColor: '{primary.600}',
              activeBorderColor: '{primary.400}',
            }
          },
          light: {
            activeBar: { background: '{primary.500}' },
            tab: {
              color: '{primary.600}',
              activeColor: '{primary.500}',
              hoverColor: '{primary.400}',
              borderColor: '{primary.200}',
              activeBorderColor: '{primary.500}',
            }
          }
        }
      },

      /* PASSWORD */
      password: {
        colorScheme: {
          dark: {
            meter: {
              background: '{primary.600}',
            }
          },
          light: {
            meter: {
              background: '{primary.100}',
            }
          }
        }
      },

      /* CHART */
      chart: {
        root: {
          background: 'transparent',
          color: '{text.color}'
        },
        legend: {
          background: 'transparent',
          color: '{text.muted.color}'
        }
      }
    }
  };
}
function buildBrandOverrides(brand = 'purple'): ComponentsOverrides {
  return {
    semantic: {
      primary: buildPrimaryScale(brand),
      secondary: buildSecondaryScale(),
      colorScheme: {
        light: {
          primary: {
            color: '{primary.600}',
            hoverColor: '{primary.700}',
            activeColor: '{primary.800}',
            'contrast.color': '#ffffff'
          },
          surface: {
            0: '{surface.0}',
            50: '{surface.50}',
            100: '{surface.100}',
            200: '{surface.200}',
            300: '{surface.300}',
            400: '{surface.400}',
            500: '{surface.500}',
            600: '{surface.600}',
            700: '{surface.700}',
            800: '{surface.800}',
            900: '{surface.900}',
            border: '{surface.border.color}'
          },
          text: {
            color: '{text.color}',
            mutedColor: '{text.muted.color}'
          }
        },

        dark: {
          primary: {
            color: '{primary.400}',
            hoverColor: '{primary.300}',
            activeColor: '{primary.200}',
            'contrast.color': '{surface.950}'
          },
          surface: {
            0: '{surface.950}',
            50: '{surface.900}',
            100: '{surface.800}',
            200: '{surface.700}',
            300: '{surface.600}',
            400: '{surface.500}',
            500: '{surface.400}',
            600: '{surface.600}',
            700: '{surface.700}',
            800: '{surface.800}',
            900: '{surface.900}',
            border: '{surface.border.color}'
          },
          text: {
            color: '{text.color}',
            mutedColor: '{text.muted.color}'
          }
        }
      },

      focusRing: {
        width: '2px',
        style: 'solid',
        color: '{primary.color}',
        offset: '2px'
      },

      border: {
        radius: {
          sm: '{border.radius.sm}',
          md: '{border.radius.md}',
          lg: '{border.radius.lg}',
          full: '{border.radius.full}'
        }
      },

      spacing: {
        1: '{spacing.1}',
        2: '{spacing.2}',
        3: '{spacing.3}',
        4: '{spacing.4}',
        5: '{spacing.5}',
        6: '{spacing.6}'
      },

      shadow: {
        1: '{shadow.1}',
        2: '{shadow.2}',
        3: '{shadow.3}',
        4: '{shadow.4}'
      }
    }
  };
}



export type BrandKey = keyof typeof BRAND_PALETTES;
type PresetName = keyof typeof BASE_PRESETS | 'Aura';


export const useThemeStore = defineStore('theme', () => {
  const presetName = ref("Aura")
  const dark = ref(false)
  const brand = ref<BrandKey>('purple')

  const buildActivePreset = () => {
    const base = BASE_PRESETS[presetName.value as keyof typeof BASE_PRESETS];
    const brandOverrides = buildBrandOverrides(brand.value);
    const componentOverrides = buildComponentOverrides()
    const composed = definePreset(base, { ...brandOverrides, ...componentOverrides })
    return composed;
  }

  const apply = () => {
    const preset = buildActivePreset();
    usePreset(preset);
    document.documentElement.classList.toggle('application', dark.value);
  }

  const setPreset = (name: PresetName) => {
    presetName.value = name;
    apply();
  }

  const setDark = (enabled: boolean) => {
    dark.value = enabled;
    apply();
  }

  const setBrand = (br: BrandKey) => {
    brand.value = br;
    apply();
  }

  return {
    presetName,
    dark,
    brand,

    apply,
    setDark,
    setPreset,
    setBrand,
  }
},
  {
    persist: true
  }
);
